import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ScreenFrame } from "@/components/layout/ScreenFrame";
import { PAD_UNDER_TAB_BAR, Screen } from "@/components/ui/v2";
import { TabBar, type TabBarTabId } from "@/components/today/v2";
import { useDocumentTheme } from "@/hooks/useDocumentTheme";
import { useHaptics } from "@/hooks/telegram";

export interface YouFrameProps {
  /**
   * Mounts the app's floating tab bar with You active. 04-YOU.md `NO_TABBAR`
   * (`you-screen.jsx:328`) lists the screens that go without — calc, shared
   * and error — so this defaults **on** and only those pass `false`. The
   * `PAD_UNDER_TAB_BAR` scroll spacer follows it: a bar-less screen gets the prototype's
   * `pad={40}` instead.
   * @default true
   */
  bar?: boolean;
  /**
   * Rendered as a sibling of the scroller, not inside it — the
   * confirm-delete sheet is a sibling of `<Screen>` in the prototype's
   * `Frame`, so it is not laid out against a scrolled box and cannot be
   * clipped by one.
   *
   * A mounted overlay is the fourth `NO_TABBAR` entry (`confirm-delete`): the
   * sheet takes the bar with it. It does **not** touch the spacer — the
   * prototype keeps drawing the Root at its normal `pad` behind the scrim,
   * and shrinking it would slide the content up 70px as the sheet opens.
   */
  overlay?: ReactNode;
  children: ReactNode;
}

/**
 * The frame every screen in the You stack renders inside — ground, sky,
 * glow, grain from `ScreenFrame`, one scroller from `Screen`, and the tab
 * bar when the screen is allowed one.
 *
 * The theme is whatever `startThemeRuntime` has settled on; no screen in
 * this stack picks its own. The glow stays on the frame (unlike Today,
 * which draws its own inside the scroller) because nothing here has a
 * dot-work stage for the light to belong to.
 */
/** The bar, and the two router hooks it needs, in their own component.
 *
 * Deliberately not inlined into `YouFrame`: hooks cannot be called
 * conditionally, and `useNavigate` throws outside a `<Router>`. The
 * bar-less screens in this stack include `/error`, which the app's error
 * boundary can render when the router itself is what failed. */
function YouTabBar(): JSX.Element {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const haptics = useHaptics();

  const onTab = (id: TabBarTabId): void => {
    if (id === "you") return;
    haptics.impact("light");
    navigate(id === "today" ? "/today" : "/friends");
  };

  return (
    <TabBar
      tabs={[
        { id: "today", label: t("bar.today") },
        { id: "friends", label: t("bar.friends") },
        { id: "you", label: t("bar.you") },
      ]}
      active="you"
      onChange={onTab}
    />
  );
}

export function YouFrame({
  bar = true,
  overlay,
  children,
}: YouFrameProps): JSX.Element {
  const theme = useDocumentTheme();

  return (
    <ScreenFrame className="d-you" theme={theme}>
      <Screen pad={bar ? PAD_UNDER_TAB_BAR : 40}>{children}</Screen>
      {overlay}
      {bar && !overlay ? <YouTabBar /> : null}
    </ScreenFrame>
  );
}

export default YouFrame;
